import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { money, fmtDate, uploadReceipt, receiptHref } from "@/mechanic/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, PackagePlus, History, Droplets } from "lucide-react";
import Expenses from "@/mechanic/pages/Expenses";

const n = (v) => Number(v || 0);

function ReceiptField({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try { const r = await uploadReceipt(file); onChange({ url: r.url, name: r.name || file.name }); toast.success("Receipt attached"); }
    catch { toast.error("Upload failed"); }
    setBusy(false); e.target.value = "";
  };
  return (
    <div className="space-y-1">
      <Label className="label-mono">Receipt (photo / PDF)</Label>
      <div className="flex items-center gap-2">
        <label className="cursor-pointer">
          <span className="inline-flex items-center h-9 px-3 rounded-md border border-border text-sm hover:bg-accent">{busy ? "Uploading…" : "Choose file"}</span>
          <input type="file" accept="image/*,.pdf" className="hidden" onChange={onFile} data-testid="receipt-file-input" />
        </label>
        {value?.url && <a href={receiptHref(value.url)} target="_blank" rel="noreferrer" className="text-sm text-primary underline truncate max-w-[180px]">{value.name || "Receipt"}</a>}
      </div>
    </div>
  );
}

// ---------------- PARTS / SUPPLIES TAB ----------------
function PartsTab({ kind }) {
  const qc = useQueryClient();
  const [dlg, setDlg] = useState(null); // {mode: 'edit'|'restock'|'history', item}
  const [form, setForm] = useState({});
  const [receipt, setReceipt] = useState(null);

  const { data: parts = [] } = useQuery({ queryKey: ["mech-parts", kind], queryFn: async () => (await api.get("/parts", { params: { kind } })).data });
  const { data: moves = [] } = useQuery({
    queryKey: ["mech-part-moves", dlg?.item?.id],
    queryFn: async () => (await api.get(`/parts/${dlg.item.id}/moves`)).data,
    enabled: dlg?.mode === "history",
  });

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["mech-parts"] }); qc.invalidateQueries({ queryKey: ["mech-alerts"] }); qc.invalidateQueries({ queryKey: ["mech-parts-all"] }); };

  const saveMut = useMutation({
    mutationFn: async () => {
      const body = { ...form, kind, qty_on_hand: n(form.qty_on_hand), reorder_threshold: n(form.reorder_threshold), unit_cost: n(form.unit_cost), sell_price: n(form.sell_price) };
      if (dlg.item) return api.put(`/parts/${dlg.item.id}`, body);
      return api.post("/parts", body);
    },
    onSuccess: () => { invalidate(); toast.success("Saved"); setDlg(null); },
    onError: () => toast.error("Save failed"),
  });
  const restockMut = useMutation({
    mutationFn: async () => api.post(`/parts/${dlg.item.id}/restock`, { qty: n(form.qty), unit_cost: form.unit_cost === "" ? null : n(form.unit_cost), receipt_url: receipt?.url || "", receipt_name: receipt?.name || "", note: form.note || "" }),
    onSuccess: () => { invalidate(); toast.success("Stock added"); setDlg(null); },
    onError: () => toast.error("Restock failed"),
  });
  const delMut = useMutation({
    mutationFn: async (id) => api.delete(`/parts/${id}`),
    onSuccess: () => { invalidate(); toast.success("Deleted"); },
  });

  const openEdit = (item) => { setForm(item ? { ...item } : { name: "", part_number: "", category: "", qty_on_hand: "", reorder_threshold: "", unit_cost: "", sell_price: "", supplier: "", location: "", unit: "", notes: "" }); setDlg({ mode: "edit", item }); };
  const openRestock = (item) => { setForm({ qty: "", unit_cost: "", note: "" }); setReceipt(null); setDlg({ mode: "restock", item }); };

  const noun = kind === "supply" ? "supply" : "part";
  return (
    <div>
      <div className="flex justify-end mb-3">
        <Button size="sm" onClick={() => openEdit(null)} data-testid={`add-${noun}-btn`}><Plus className="h-4 w-4 mr-1" />Add {noun}</Button>
      </div>
      <Card className="overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Name</TableHead><TableHead>Part #</TableHead><TableHead>Category</TableHead>
            <TableHead className="text-right">On hand</TableHead><TableHead className="text-right">Unit cost</TableHead>
            <TableHead className="text-right">Sell</TableHead><TableHead>Supplier</TableHead><TableHead>Location</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {parts.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No {noun === "supply" ? "shop supplies" : "parts"} yet.</TableCell></TableRow>
            ) : parts.map((p) => (
              <TableRow key={p.id} data-testid={`part-row-${p.id}`}>
                <TableCell className="font-medium">{p.name}{p.low_stock && <Badge className="ml-2 border-0 bg-red-500/12 text-red-400 text-[10px]">LOW</Badge>}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{p.part_number || "—"}</TableCell>
                <TableCell className="text-sm">{p.category || "—"}</TableCell>
                <TableCell className="text-right font-mono-data">{p.qty_on_hand}{p.unit ? ` ${p.unit}` : ""}</TableCell>
                <TableCell className="text-right font-mono-data">{money(p.unit_cost)}</TableCell>
                <TableCell className="text-right font-mono-data">{money(p.sell_price)}</TableCell>
                <TableCell className="text-sm">{p.supplier || "—"}</TableCell>
                <TableCell className="text-sm">{p.location || "—"}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="icon" title="Restock" onClick={() => openRestock(p)} data-testid={`restock-${p.id}`}><PackagePlus className="h-4 w-4 text-green-400" /></Button>
                  <Button variant="ghost" size="icon" title="History" onClick={() => setDlg({ mode: "history", item: p })} data-testid={`history-${p.id}`}><History className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`edit-part-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => window.confirm("Delete this item?") && delMut.mutate(p.id)} data-testid={`del-part-${p.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Edit dialog */}
      <Dialog open={dlg?.mode === "edit"} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{dlg?.item ? `Edit ${noun}` : `Add ${noun}`}</DialogTitle><DialogDescription>Inventory item details.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1"><Label className="label-mono">Name *</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="part-name-input" /></div>
            <div className="space-y-1"><Label className="label-mono">Part #</Label><Input value={form.part_number || ""} onChange={(e) => setForm({ ...form, part_number: e.target.value })} /></div>
            <div className="space-y-1"><Label className="label-mono">Category</Label><Input value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Brakes, Filters…" /></div>
            <div className="space-y-1"><Label className="label-mono">Qty on hand</Label><Input type="number" value={form.qty_on_hand} onChange={(e) => setForm({ ...form, qty_on_hand: e.target.value })} data-testid="part-qty-input" /></div>
            <div className="space-y-1"><Label className="label-mono">Reorder at</Label><Input type="number" value={form.reorder_threshold} onChange={(e) => setForm({ ...form, reorder_threshold: e.target.value })} data-testid="part-threshold-input" /></div>
            <div className="space-y-1"><Label className="label-mono">Unit cost ($)</Label><Input type="number" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} data-testid="part-cost-input" /></div>
            <div className="space-y-1"><Label className="label-mono">Sell price ($)</Label><Input type="number" value={form.sell_price} onChange={(e) => setForm({ ...form, sell_price: e.target.value })} /></div>
            <div className="space-y-1"><Label className="label-mono">Supplier</Label><Input value={form.supplier || ""} onChange={(e) => setForm({ ...form, supplier: e.target.value })} /></div>
            <div className="space-y-1"><Label className="label-mono">Shelf / Location</Label><Input value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
            <div className="space-y-1"><Label className="label-mono">Unit</Label><Input value={form.unit || ""} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="pcs, box, bottle" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Cancel</Button>
            <Button onClick={() => saveMut.mutate()} disabled={!form.name || saveMut.isPending} data-testid="part-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restock dialog */}
      <Dialog open={dlg?.mode === "restock"} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Restock — {dlg?.item?.name}</DialogTitle><DialogDescription>Add received stock (use a negative qty to correct).</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label className="label-mono">Quantity received *</Label><Input type="number" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} data-testid="restock-qty-input" /></div>
            <div className="space-y-1"><Label className="label-mono">New unit cost ($) <span className="text-xs text-muted-foreground">(optional)</span></Label><Input type="number" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} /></div>
            <ReceiptField value={receipt} onChange={setReceipt} />
            <div className="space-y-1"><Label className="label-mono">Note</Label><Input value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Cancel</Button>
            <Button onClick={() => restockMut.mutate()} disabled={!form.qty || restockMut.isPending} data-testid="restock-save-btn">Add stock</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={dlg?.mode === "history"} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Stock history — {dlg?.item?.name}</DialogTitle><DialogDescription>Every movement, in and out.</DialogDescription></DialogHeader>
          <div className="divide-y divide-border text-sm">
            {moves.length === 0 ? <p className="py-4 text-muted-foreground">No movements yet.</p> : moves.map((m) => (
              <div key={m.id} className="py-2 flex items-center justify-between gap-2">
                <div>
                  <p><b className={m.qty_change > 0 ? "text-green-400" : "text-red-400"}>{m.qty_change > 0 ? "+" : ""}{m.qty_change}</b> · {m.type}</p>
                  <p className="text-xs text-muted-foreground">{fmtDate(m.created_at)} — {m.note}</p>
                </div>
                {m.receipt_url && <a href={receiptHref(m.receipt_url)} target="_blank" rel="noreferrer" className="text-xs text-primary underline">Receipt</a>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- OILS TAB ----------------
function OilsTab() {
  const qc = useQueryClient();
  const [dlg, setDlg] = useState(null);
  const [form, setForm] = useState({});
  const [receipt, setReceipt] = useState(null);

  const { data: oils = [] } = useQuery({ queryKey: ["mech-oils"], queryFn: async () => (await api.get("/oils")).data });
  const { data: logs = [] } = useQuery({
    queryKey: ["mech-oil-logs", dlg?.item?.id],
    queryFn: async () => (await api.get(`/oils/${dlg.item.id}/logs`)).data,
    enabled: dlg?.mode === "history",
  });

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["mech-oils"] }); qc.invalidateQueries({ queryKey: ["mech-alerts"] }); };

  const saveMut = useMutation({
    mutationFn: async () => {
      const body = { ...form, stock_litres: n(form.stock_litres), cost_per_litre: n(form.cost_per_litre), reorder_threshold_litres: n(form.reorder_threshold_litres), capacity_litres: n(form.capacity_litres) };
      if (dlg.item) return api.put(`/oils/${dlg.item.id}`, body);
      return api.post("/oils", body);
    },
    onSuccess: () => { invalidate(); toast.success("Saved"); setDlg(null); },
    onError: () => toast.error("Save failed"),
  });
  const restockMut = useMutation({
    mutationFn: async () => api.post(`/oils/${dlg.item.id}/restock`, { litres: n(form.litres), cost_per_litre: form.cost_per_litre === "" ? null : n(form.cost_per_litre), receipt_url: receipt?.url || "", receipt_name: receipt?.name || "", note: form.note || "" }),
    onSuccess: () => { invalidate(); toast.success("Oil stock added"); setDlg(null); },
    onError: () => toast.error("Restock failed"),
  });
  const delMut = useMutation({ mutationFn: async (id) => api.delete(`/oils/${id}`), onSuccess: () => { invalidate(); toast.success("Deleted"); } });

  const openEdit = (item) => { setForm(item ? { ...item } : { name: "", stock_litres: "", cost_per_litre: "", reorder_threshold_litres: "", capacity_litres: "", supplier: "", notes: "" }); setDlg({ mode: "edit", item }); };
  const openRestock = (item) => { setForm({ litres: "", cost_per_litre: "", note: "" }); setReceipt(null); setDlg({ mode: "restock", item }); };

  return (
    <div>
      <div className="flex justify-end mb-3">
        <Button size="sm" onClick={() => openEdit(null)} data-testid="add-oil-btn"><Plus className="h-4 w-4 mr-1" />Add oil type</Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {oils.length === 0 && <Card className="p-6 text-sm text-muted-foreground col-span-full">No oils yet. Add your oil types (e.g. 5W-30 barrel) to start tracking usage per job.</Card>}
        {oils.map((o) => {
          const cap = n(o.capacity_litres);
          const pct = cap > 0 ? Math.max(0, Math.min(100, (n(o.stock_litres) / cap) * 100)) : null;
          return (
            <Card key={o.id} className="p-5" data-testid={`oil-card-${o.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Droplets className="h-5 w-5 text-blue-500" />
                  <div>
                    <p className="font-heading font-bold">{o.name}</p>
                    <p className="text-xs text-muted-foreground">{money(o.cost_per_litre)}/L{o.supplier ? ` · ${o.supplier}` : ""}</p>
                  </div>
                </div>
                {o.low_stock && <Badge className="border-0 bg-red-500/12 text-red-400">REORDER</Badge>}
              </div>
              <p className="text-2xl font-heading font-bold font-mono-data">{Number(o.stock_litres).toFixed(1)} L</p>
              {pct !== null && (
                <div className="mt-2 h-2 rounded-full bg-secondary overflow-hidden">
                  <div className={`h-full ${pct <= 25 ? "bg-red-500" : pct <= 50 ? "bg-amber-500" : "bg-green-500"}`} style={{ width: `${pct}%` }} />
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                {cap > 0 ? `of ${cap}L barrel · ` : ""}reorder at {o.reorder_threshold_litres || 0}L · value {money(n(o.stock_litres) * n(o.cost_per_litre))}
              </p>
              <div className="flex gap-1 mt-3">
                <Button size="sm" variant="outline" onClick={() => openRestock(o)} data-testid={`restock-oil-${o.id}`}><PackagePlus className="h-3.5 w-3.5 mr-1" />Restock</Button>
                <Button size="sm" variant="ghost" onClick={() => setDlg({ mode: "history", item: o })} data-testid={`oil-history-${o.id}`}><History className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="ghost" onClick={() => openEdit(o)} data-testid={`edit-oil-${o.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="ghost" onClick={() => window.confirm("Delete this oil?") && delMut.mutate(o.id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={dlg?.mode === "edit"} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{dlg?.item ? "Edit oil" : "Add oil type"}</DialogTitle><DialogDescription>Track a barrel/drum by litres and cost.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1"><Label className="label-mono">Name *</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="5W-30 Synthetic" data-testid="oil-name-input" /></div>
            <div className="space-y-1"><Label className="label-mono">Stock (litres)</Label><Input type="number" value={form.stock_litres} onChange={(e) => setForm({ ...form, stock_litres: e.target.value })} data-testid="oil-stock-input" /></div>
            <div className="space-y-1"><Label className="label-mono">Cost per litre ($)</Label><Input type="number" value={form.cost_per_litre} onChange={(e) => setForm({ ...form, cost_per_litre: e.target.value })} data-testid="oil-cost-input" /></div>
            <div className="space-y-1"><Label className="label-mono">Reorder at (litres)</Label><Input type="number" value={form.reorder_threshold_litres} onChange={(e) => setForm({ ...form, reorder_threshold_litres: e.target.value })} data-testid="oil-threshold-input" /></div>
            <div className="space-y-1"><Label className="label-mono">Barrel size (litres)</Label><Input type="number" value={form.capacity_litres} onChange={(e) => setForm({ ...form, capacity_litres: e.target.value })} /></div>
            <div className="col-span-2 space-y-1"><Label className="label-mono">Supplier</Label><Input value={form.supplier || ""} onChange={(e) => setForm({ ...form, supplier: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Cancel</Button>
            <Button onClick={() => saveMut.mutate()} disabled={!form.name || saveMut.isPending} data-testid="oil-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg?.mode === "restock"} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Restock — {dlg?.item?.name}</DialogTitle><DialogDescription>Log a new barrel / top-up.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label className="label-mono">Litres received *</Label><Input type="number" value={form.litres} onChange={(e) => setForm({ ...form, litres: e.target.value })} data-testid="oil-restock-litres" /></div>
            <div className="space-y-1"><Label className="label-mono">New cost per litre ($) <span className="text-xs text-muted-foreground">(optional)</span></Label><Input type="number" value={form.cost_per_litre} onChange={(e) => setForm({ ...form, cost_per_litre: e.target.value })} /></div>
            <ReceiptField value={receipt} onChange={setReceipt} />
            <div className="space-y-1"><Label className="label-mono">Note</Label><Input value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Cancel</Button>
            <Button onClick={() => restockMut.mutate()} disabled={!form.litres || restockMut.isPending} data-testid="oil-restock-save">Add oil</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg?.mode === "history"} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Usage log — {dlg?.item?.name}</DialogTitle><DialogDescription>Litres in and out, per job.</DialogDescription></DialogHeader>
          <div className="divide-y divide-border text-sm">
            {logs.length === 0 ? <p className="py-4 text-muted-foreground">No activity yet.</p> : logs.map((m) => (
              <div key={m.id} className="py-2 flex items-center justify-between gap-2">
                <div>
                  <p><b className={m.litres_change > 0 ? "text-green-400" : "text-red-400"}>{m.litres_change > 0 ? "+" : ""}{Number(m.litres_change).toFixed(1)}L</b> · {m.type} · {money(Math.abs(m.cost || 0))}</p>
                  <p className="text-xs text-muted-foreground">{fmtDate(m.created_at)} — {m.note}</p>
                </div>
                {m.receipt_url && <a href={receiptHref(m.receipt_url)} target="_blank" rel="noreferrer" className="text-xs text-primary underline">Receipt</a>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Inventory() {
  return (
    <div className="p-8" data-testid="mech-inventory-page">
      <h1 className="font-heading text-3xl mb-1">Inventory &amp; Expenses</h1>
      <p className="text-muted-foreground mb-6">Parts, shop supplies and oils (auto-deduct when used on a work order) plus one-off + recurring shop expenses.</p>
      <Tabs defaultValue="oils">
        <TabsList className="mb-4">
          <TabsTrigger value="oils" data-testid="tab-oils">Oils &amp; Fluids</TabsTrigger>
          <TabsTrigger value="parts" data-testid="tab-parts">Parts</TabsTrigger>
          <TabsTrigger value="supplies" data-testid="tab-supplies">Shop Supplies</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses">Expenses</TabsTrigger>
        </TabsList>
        <TabsContent value="oils"><OilsTab /></TabsContent>
        <TabsContent value="parts"><PartsTab kind="part" /></TabsContent>
        <TabsContent value="supplies"><PartsTab kind="supply" /></TabsContent>
        <TabsContent value="expenses"><Expenses embedded /></TabsContent>
      </Tabs>
    </div>
  );
}
